import { useCallback, useMemo, useState } from "react";

import {
  useImageProcessing,
  type ImageProcessingState,
} from "@/hooks/useImageProcessing";
import { imageUploadService } from "@/services/image-upload.service";
import { useAddObjectFormStore } from "@/store/addObjectForm.store";

interface UseFormImageQueueOptions {
  propertyId?: string;
  targetFolderType?: string;
  onComplete?: (urls: string[]) => void;
  onError?: (error: string) => void;
}

interface UseFormImageQueueReturn {
  status: ImageProcessingState["status"];
  progress: number;
  completedImages: string[];
  start: (urls: string[]) => Promise<void>;
  reset: () => void;
}

// Hook that wraps backend queue-processing endpoint and internal polling
export function useFormImageQueue(
  options: UseFormImageQueueOptions = {},
): UseFormImageQueueReturn {
  const { onComplete, onError, propertyId, targetFolderType } = options;

  const [jobId, setJobId] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);

  const {
    state: processingState,
    startPolling,
    reset: resetProcessing,
  } = useImageProcessing({
    onComplete: (imgs) => {
      onComplete?.(imgs);
    },
    onError,
  });

  const start = useCallback(
    async (urls: string[]) => {
      if (!urls.length) return;
      if (queued) return;

      // Filter to session-specific temp urls only
      const { sessionTempUrls, clearSessionTempUrls } =
        useAddObjectFormStore.getState();

      const sessionUrls = urls.filter((u) => sessionTempUrls.includes(u));

      if (sessionUrls.length === 0) return;

      const id = await imageUploadService.queueProcessing(sessionUrls, {
        propertyId,
        targetFolderType,
      });
      if (id) {
        setQueued(true);
        setJobId(id);
        startPolling(id);

        // Clear session temp set after successfully queueing
        clearSessionTempUrls();
      }
    },
    [queued, propertyId, targetFolderType, startPolling],
  );

  const reset = useCallback(() => {
    setQueued(false);
    setJobId(null);
    resetProcessing();
  }, [resetProcessing]);

  const status = useMemo(
    () => processingState.status,
    [processingState.status],
  );

  return {
    status,
    progress: processingState.progress,
    completedImages: processingState.completedImages,
    start,
    reset,
  };
}
